import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

/**
 * Modal controller: a single stack owns which modals are open, in what order,
 * and (implicitly) what z-index each gets. Replaces scattered
 * `useState`/`setShow...` pairs in AppContent + LandingPage plus hardcoded
 * z-index rules in each modal's SCSS.
 *
 * See `docs/superpowers/specs/2026-04-20-modal-controller-design.md` for
 * the full design, migration plan, and rationale.
 */

export type ModalId =
  | 'dms'
  | 'building'
  | 'listing'
  | 'profile'
  | 'landingMenu'
  | 'signupLogin'
  | 'marketingConsent'
  | 'landingCouldBuyOrCouldSell'
  | 'shareMatch';

export interface ModalEntry {
  id: ModalId;
  /** Arbitrary props passed to the rendered modal component. */
  props: Record<string, unknown>;
  /** Stable React key so stack-position changes don't force a remount. */
  key: string;
}

export interface ModalAPI {
  isOpen: (id: ModalId) => boolean;

  /**
   * Push a modal onto the stack, or update its props if already open.
   * If the same id is already topmost, this is a no-op for the stack
   * order but updates props.
   */
  open: (id: ModalId, props?: Record<string, unknown>) => void;

  /** Remove the given modal from the stack. No-op if not open. */
  close: (id: ModalId) => void;

  /**
   * Push `newId` (or update its props if already open), wait until the
   * browser has painted it, then remove `oldId`. If `oldId` is null,
   * behaves like `open(newId)`. User never sees an empty frame between
   * the two modals.
   */
  replace: (
    oldId: ModalId | null,
    newId: ModalId,
    newProps?: Record<string, unknown>,
  ) => void;
}

// Two contexts on purpose: API callbacks are stable (no deps that change),
// the stack array changes on every open/close. Splitting them means
// consumers that only need the callbacks (LandingPage, AppContent) don't
// re-run their effects every time another modal opens — which would
// otherwise cause an infinite update loop in `useEffect` blocks that
// `modals.open(...)` from inside.
const ModalApiCtx = createContext<ModalAPI | null>(null);
const ModalStackCtx = createContext<readonly ModalEntry[]>([]);

let keySeed = 0;
const genKey = () => `modal-${++keySeed}`;

export const ModalControllerProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [stack, setStack] = useState<ModalEntry[]>([]);
  const stackRef = useRef(stack);
  stackRef.current = stack;

  const isOpen = useCallback((id: ModalId) => {
    return stackRef.current.some(e => e.id === id);
  }, []);

  const open = useCallback(
    (id: ModalId, props: Record<string, unknown> = {}) => {
      setStack(prev => {
        const existing = prev.find(e => e.id === id);
        if (existing) {
          // Already open: refresh props IN PLACE — keep stack position. Re-raising
          // to the top here let a prop-synced re-open (e.g. building props change)
          // jump above a modal opened later, re-covering the header burger menu.
          // Use replace() when an intentional re-raise is actually wanted.
          return prev.map(e => (e.id === id ? { ...e, props } : e));
        }
        return [...prev, { id, props, key: genKey() }];
      });
    },
    [],
  );

  const close = useCallback((id: ModalId) => {
    setStack(prev => prev.filter(e => e.id !== id));
  }, []);

  const replace = useCallback(
    (
      oldId: ModalId | null,
      newId: ModalId,
      newProps: Record<string, unknown> = {},
    ) => {
      // Push/update newId synchronously so it mounts this frame.
      setStack(prev => {
        const existing = prev.find(e => e.id === newId);
        if (existing) {
          const without = prev.filter(e => e.id !== newId);
          return [...without, { ...existing, props: newProps }];
        }
        return [...prev, { id: newId, props: newProps, key: genKey() }];
      });

      if (!oldId || oldId === newId) return;

      // Double requestAnimationFrame: first rAF runs before the next paint,
      // second rAF runs AFTER paint. By then the new modal is on screen,
      // so removing the old one leaves no empty frame.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setStack(prev => prev.filter(e => e.id !== oldId));
        });
      });
    },
    [],
  );

  // API ref is stable for the lifetime of the provider — all four callbacks
  // are useCallback([]). Consumers can put `modals` in effect deps without
  // triggering re-runs on every stack change.
  const api = useMemo<ModalAPI>(
    () => ({ isOpen, open, close, replace }),
    [isOpen, open, close, replace],
  );

  return (
    <ModalApiCtx.Provider value={api}>
      <ModalStackCtx.Provider value={stack}>{children}</ModalStackCtx.Provider>
    </ModalApiCtx.Provider>
  );
};

export const useModals = (): ModalAPI => {
  const ctx = useContext(ModalApiCtx);
  if (!ctx) {
    throw new Error(
      'useModals must be called inside <ModalControllerProvider>',
    );
  }
  return ctx;
};

export const useModalStack = (): readonly ModalEntry[] =>
  useContext(ModalStackCtx);
